import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import { Alert, AlertDescription, Button, Spinner, Switch } from '@rentflowdev/ui'
import { splitVat } from '@rentflowdev/common/vat'
import { useGetAccountQuery, useGetMeQuery, useGetVatRateQuery } from '@/api'
import { TaxRegimeDialog } from '@/components/tax-regime-dialog'
import type { HousingType, PropertyType, VatDecision, VatReason } from '@/types'

interface VatSummaryProps {
	propertyType?: PropertyType
	housingType?:  HousingType
	/** Rent amount as typed, so the breakdown follows what the user sees. */
	amount?:       number | string
	/** Whether VAT sits inside the amount; only meaningful above 0%. */
	included:      boolean
	onIncludedChange: (included: boolean) => void
	/**
	 * A rate already frozen onto the record — a rental keeps the terms it was
	 * created on. Given one, the current regime is not consulted at all: the
	 * breakdown has to match the contract, not today's settings.
	 */
	snapshot?: { percent: number; reason?: VatReason }
}

const fmt = (value: number) => value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Read-only VAT breakdown under the rent amount.
 *
 * The rate is resolved server-side and fetched here rather than computed in
 * the browser: the matrix has one home, and a copy of it would drift. Only the
 * split itself is done locally, because it has to follow every keystroke.
 */
export function VatSummary({
	propertyType, housingType, amount, included, onIncludedChange, snapshot,
}: VatSummaryProps) {
	const { t } = useTranslation()
	// Set here instead of navigating to /settings/profile. The regime is needed
	// while the property is being filled in, and leaving for it closed the
	// wizard and threw away everything typed — with no way back to the property.
	const [regimeOpen, setRegimeOpen] = useState(false)
	// Same pair the settings page reads the regime from: the account is keyed
	// by the current user's id, which only /me carries.
	const { data: me } = useGetMeQuery()
	const { data: account } = useGetAccountQuery(me?.id ?? '', { skip: !me?.id })

	const { data: resolved, isLoading } = useGetVatRateQuery(
		{ type: propertyType as PropertyType, housingType },
		{ skip: !propertyType || snapshot !== undefined },
	)

	/**
	 * A snapshot is not a fresh decision, so it carries no `hint`: the hint
	 * explains which rule the matrix just applied, and a stored rate was
	 * decided by whatever rule held when it was agreed. This view reads
	 * `reason` instead, which is recorded.
	 */
	type Decision =
		| VatDecision
		| Omit<Extract<VatDecision, { kind: 'rate' }>, 'hint'>

	const decision: Decision | undefined = snapshot
		? {
				kind:       'rate',
				percent:    snapshot.percent as 0 | 5 | 7 | 22,
				// Placeholder only — the render below reads the stored ground via
				// `reason` and never invents one for a legacy snapshot.
				reason:     snapshot.reason ?? 'STANDARD',
				toggleable: snapshot.percent > 0,
			}
		: resolved

	if (!snapshot && !propertyType) return null
	if (isLoading) return <Spinner size="sm" />
	if (!decision) return null

	/** Mounted only while open, so it always opens on the saved values. */
	const regimeDialog = regimeOpen && (
		<TaxRegimeDialog
			onOpenChange={setRegimeOpen}
			taxRegime={account?.taxRegime ?? 'NONE'}
			usnIncomeBand={account?.usnIncomeBand}
			vatExemptArt145={account?.vatExemptArt145}
			vatVoluntary22={account?.vatVoluntary22}
		/>
	)

	const regimeButton = (labelKey: string, className: string) => (
		<Button
			variant="link"
			size="sm"
			className={className}
			onClick={() => setRegimeOpen(true)}
		>
			{t(labelKey)}
		</Button>
	)

	if (decision.kind === 'unknown') {
		return (
			<>
				<Alert variant="warning">
					<AlertCircleIcon className="size-4" />
					<AlertDescription className="flex flex-wrap items-center gap-2">
						{t(`properties.rentTerms.vat.${decision.code}`)}
						{regimeButton('properties.rentTerms.vat.setRegime', 'h-auto p-0')}
					</AlertDescription>
				</Alert>
				{regimeDialog}
			</>
		)
	}

	if (decision.kind === 'forbidden') {
		return (
			<>
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertDescription className="flex flex-wrap items-center gap-2">
						{t('properties.rentTerms.vat.npdForbidden')}
						{regimeButton('properties.rentTerms.vat.changeRegime', 'h-auto p-0')}
					</AlertDescription>
				</Alert>
				{regimeDialog}
			</>
		)
	}

	const { percent, toggleable } = decision
	// A snapshot may predate the matrix and carry no legal ground — showing
	// «Общая ставка» for its zero rate would assert a ground the record lacks.
	const reason = snapshot ? snapshot.reason : decision.reason
	// RHF hands over whatever the input holds, including '' before anything is
	// typed. Coercing here keeps a stray NaN out of the breakdown.
	const parsed = Number(amount)
	const base = Number.isFinite(parsed) ? parsed : 0
	// Same integer-kopeck arithmetic the server freezes onto records — a float
	// split here drifts a kopeck from the stored figures on tie amounts.
	const split = splitVat({ amount: base.toFixed(2), percent, included })

	return (
		<div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--color-border) p-3">
			<div className="flex items-center justify-between gap-3">
				<span className="text-sm text-(--color-muted-foreground)">
					{percent === 0 ? '' : t('properties.rentTerms.vat.rate', { percent })}
				</span>
				{/* Changing the regime does not touch a rate already frozen onto a
				    record, so offering the link there would only mislead. */}
				{!snapshot && regimeButton('properties.rentTerms.vat.changeRegime', 'h-auto p-0 text-xs')}
			</div>

			{/* At 0% the choice means nothing, so it is not offered. */}
			{toggleable && (
				<label className="flex items-center justify-between gap-3">
					<span className="text-sm">
						{included
							? t('properties.rentTerms.vat.includedInPrice')
							: t('properties.rentTerms.vat.addedOnTop')}
					</span>
					<Switch checked={included} onCheckedChange={onIncludedChange} />
				</label>
			)}

			{(percent !== 0 || reason) && (
				<p className="text-sm font-[500] text-(--color-foreground)">
					{percent === 0
						? t(`properties.rentTerms.vat.reasons.${reason}`)
						: included
							? t('properties.rentTerms.vat.breakdownIncluded', { percent, vat: fmt(Number(split.vat)) })
							: t('properties.rentTerms.vat.breakdownOnTop', {
									percent,
									vat: fmt(Number(split.vat)),
									total: fmt(Number(split.gross)),
								})}
				</p>
			)}
			{regimeDialog}
		</div>
	)
}
